import { Action, action, State, Thunk, thunk, ThunkOn, thunkOn } from 'easy-peasy';
import * as yup from 'yup';
import pkg from '../../package.json';
import { createExternalAPI } from '../api/external-api';
import { ErrorCodes } from '../errors/ErrorCodes';
import { PlayerError } from '../errors/PlayerError';
import { VideoPlayerProps } from '../typings';
import { StoreState } from './store';

export interface ConfigState {
  loaded: boolean;
  version: string;
  appName: string;
  appVersion: string;
  videoGuid: string | null;
  userGuid: string | null;
  entityId: string | null;
  dayId: string | null;
  recommendationId: string | null;
  language: string | null;
  autoplay: boolean;
  muted: boolean;
  isBODi: boolean;
  extraHeartbeatParams: VideoPlayerProps['extraHeartbeatParams'] | null;
  getAccessToken: VideoPlayerProps['getAccessToken'] | null;
  closeCallback: (() => any) | null;
  onPlayCallback: (() => any) | null;
  onPauseCallback: (() => any) | null;
  onStopCallback: (() => any) | null;
  onSeekCallback: ((timecode: number) => any) | null;
  onKeyPressUpCallback: (() => any) | null;
  onProgressCallback: ((timecode: number) => any) | null;
  onResumeOverlayChangeCallback: ((visible: boolean) => any) | null;
  onAnyActionCallback: ((action: { type: string; payload: any }) => any) | null;
  onErrorCallback: ((error: PlayerError) => any) | null;
  cocoaApiUrl: string | null;
  cocoaApiKey: string | null;
  videoApiRetryAttempts: number;
  vhsApiUrl: string | null;
  vhsApiKey: string | null;
  muxEnvKey: string | null;
  muxPlayerName: string | null;
  muxDisableCookies: boolean;
  muxRespectDoNotTrack: boolean;
  muxIsEnabled: boolean;
  tracksIsEnabled: boolean;
  chromecastAppId: string | null;
  playbackControlsEnabled: boolean;
  debug: boolean;
  logging: boolean;
  useBodwebHeader: boolean;
  simulateVideoApiAuthorizer: boolean;
  skipResume: boolean;
  maxBitrateForEdge: number | null;

  setLoaded: Action<ConfigState, boolean>;
  setAppName: Action<ConfigState, string>;
  setAppVersion: Action<ConfigState, string>;
  setVideoGuid: Action<ConfigState, string | null>;
  setUserGuid: Action<ConfigState, string | null>;
  setEntityId: Action<ConfigState, string | null>;
  setDayId: Action<ConfigState, string | null>;
  setRecommendationId: Action<ConfigState, string | null>;
  setLanguage: Action<ConfigState, string | null>;
  setAutoplay: Action<ConfigState, boolean>;
  setMuted: Action<ConfigState, boolean>;
  setIsBODi: Action<ConfigState, boolean>;
  setExtraHeartbeatParams: Action<ConfigState, ConfigState['extraHeartbeatParams']>;
  setGetAccessToken: Action<ConfigState, (() => Promise<{ accessToken: string; tokenType: string }>) | null>;
  setCloseCallback: Action<ConfigState, (() => any) | null>;
  setOnPlayCallback: Action<ConfigState, (() => any) | null>;
  setOnPauseCallback: Action<ConfigState, (() => any) | null>;
  setOnStopCallback: Action<ConfigState, (() => any) | null>;
  setOnSeekCallback: Action<ConfigState, ((timecode: number) => any) | null>;
  setOnKeyPressUpCallback: Action<ConfigState, (() => any) | null>;
  setOnProgressCallback: Action<ConfigState, ((timecode: number) => any) | null>;
  setOnResumeOverlayChangeCallback: Action<ConfigState, ((visible: boolean) => any) | null>;
  setOnAnyActionCallback: Action<ConfigState, ConfigState['onAnyActionCallback']>;
  setOnErrorCallback: Action<ConfigState, ((error: PlayerError) => any) | null>;
  setCocoaApiUrl: Action<ConfigState, string | null>;
  setCocoaApiKey: Action<ConfigState, string | null>;
  setVideoApiRetryAttempts: Action<ConfigState, number>;
  setVhsApiUrl: Action<ConfigState, string | null>;
  setVhsApiKey: Action<ConfigState, string | null>;
  setMuxEnvKey: Action<ConfigState, string | null>;
  setMuxPlayerName: Action<ConfigState, string | null>;
  setMuxDisableCookies: Action<ConfigState, boolean>;
  setMuxRespectDoNotTrack: Action<ConfigState, boolean>;
  setMuxIsEnabled: Action<ConfigState, boolean>;
  setTracksIsEnabled: Action<ConfigState, boolean>;
  setChromecastAppId: Action<ConfigState, string | null>;
  setPlaybackControlsEnabled: Action<ConfigState, boolean>;
  setDebug: Action<ConfigState, boolean>;
  setLogging: Action<ConfigState, boolean>;
  setUseBodwebHeader: Action<ConfigState, boolean>;
  setSimulateVideoApiAuthorizer: Action<ConfigState, boolean>;
  setSkipResume: Action<ConfigState, boolean>;
  setMaxBitrateForEdge: Action<ConfigState, number | null>;
  load: Thunk<ConfigState, VideoPlayerProps, any, StoreState>;
  reset: Action<ConfigState>;
  onPlayerDestroy: ThunkOn<ConfigState, any, StoreState>;
}

const initialState: State<ConfigState> = {
  loaded: false,
  version: pkg.version,
  appName: 'web-video-player',
  appVersion: pkg.version,
  videoGuid: null,
  userGuid: null,
  entityId: null,
  dayId: null,
  recommendationId: null,
  language: null,
  autoplay: true,
  muted: false,
  isBODi: false,
  extraHeartbeatParams: null,
  getAccessToken: null,
  closeCallback: null,
  onPlayCallback: null,
  onPauseCallback: null,
  onStopCallback: null,
  onSeekCallback: null,
  onKeyPressUpCallback: null,
  onProgressCallback: null,
  onResumeOverlayChangeCallback: null,
  onAnyActionCallback: null,
  onErrorCallback: null,
  cocoaApiUrl: null,
  cocoaApiKey: null,
  videoApiRetryAttempts: 1,
  vhsApiUrl: null,
  vhsApiKey: null,
  muxEnvKey: null,
  muxPlayerName: 'bodi-web',
  muxDisableCookies: false,
  muxRespectDoNotTrack: true,
  muxIsEnabled: true,
  tracksIsEnabled: false,
  chromecastAppId: null,
  playbackControlsEnabled: true,
  debug: false,
  logging: false,
  useBodwebHeader: true,
  simulateVideoApiAuthorizer: false,
  skipResume: false,
  maxBitrateForEdge: null,
};

export const configState: ConfigState = {
  /*================
   * State      
   ================*/
  ...initialState,

  /*================
   * Actions      
   ================*/

  load: thunk((actions, videoPlayerProps, { getStoreActions, getStoreState }) => {
    try {
      configValidationSchema.validateSync(videoPlayerProps);
      actions.setAppName(videoPlayerProps.appName ?? 'web-video-player');
      actions.setAppVersion(videoPlayerProps.appVersion ?? getStoreState().config.version);
      actions.setVideoGuid(videoPlayerProps.videoGUID);
      actions.setUserGuid(videoPlayerProps.userGUID || null);
      actions.setEntityId(videoPlayerProps.entityId || null);
      actions.setDayId(videoPlayerProps.dayId || null);
      actions.setRecommendationId(videoPlayerProps.recommendationId || null);
      actions.setLanguage(videoPlayerProps.language || 'en_US');
      actions.setAutoplay(videoPlayerProps.autoplay ?? true);
      actions.setMuted(videoPlayerProps.muted ?? false);
      actions.setIsBODi(videoPlayerProps.isBODi ?? false);
      actions.setExtraHeartbeatParams(videoPlayerProps.extraHeartbeatParams || null);
      actions.setGetAccessToken(videoPlayerProps.getAccessToken || null);
      actions.setCloseCallback(videoPlayerProps.closeCallback || null);
      actions.setOnPlayCallback(videoPlayerProps.onPlay || null);
      actions.setOnPauseCallback(videoPlayerProps.onPause || null);
      actions.setOnStopCallback(videoPlayerProps.onStop || null);
      actions.setOnSeekCallback(videoPlayerProps.onSeek || null);
      actions.setOnKeyPressUpCallback(videoPlayerProps.onKeyPressUp || null);
      actions.setOnProgressCallback(videoPlayerProps.onProgress || null);
      actions.setOnResumeOverlayChangeCallback(videoPlayerProps.onResumeOverlayChange || null);
      actions.setOnAnyActionCallback(videoPlayerProps.onAnyAction || null);
      actions.setOnErrorCallback(videoPlayerProps.onError || null);
      actions.setCocoaApiUrl(videoPlayerProps.config.cocoaApiUrl || null);
      actions.setCocoaApiKey(videoPlayerProps.config.cocoaApiKey || null);
      actions.setVideoApiRetryAttempts(videoPlayerProps.config.videoApiRetryAttempts ?? 1);
      actions.setVhsApiUrl(videoPlayerProps.config.vhsApiUrl);
      actions.setVhsApiKey(videoPlayerProps.config.vhsApiKey);
      actions.setMuxEnvKey(videoPlayerProps.config.muxEnvKey ?? null);
      actions.setMuxPlayerName(videoPlayerProps.config.muxPlayerName ?? 'bodi-web');
      actions.setMuxDisableCookies(videoPlayerProps.config.muxDisableCookies ?? false);
      actions.setMuxRespectDoNotTrack(videoPlayerProps.config.muxRespectDoNotTrack ?? true);
      actions.setMuxIsEnabled(videoPlayerProps.config.muxIsEnabled ?? true);
      actions.setTracksIsEnabled(videoPlayerProps.config.tracksIsEnabled ?? false);
      actions.setChromecastAppId(videoPlayerProps.config.chromecastAppId ?? null);
      actions.setPlaybackControlsEnabled(videoPlayerProps.config.playbackControlsEnabled ?? true);
      actions.setDebug(videoPlayerProps.config.debug ?? false);
      actions.setLogging(videoPlayerProps.config.logging ?? false);
      actions.setUseBodwebHeader(videoPlayerProps.config.useBodwebHeader ?? true);
      actions.setSimulateVideoApiAuthorizer(videoPlayerProps.config.simulateVideoApiAuthorizer ?? false);
      actions.setSkipResume(videoPlayerProps.config.skipResume ?? false);
      actions.setLoaded(true);
      actions.setMaxBitrateForEdge(videoPlayerProps.config.maxBitrateForEdge || null);

      if (videoPlayerProps.externalApi) {
        const storeInstance = {
          getState: getStoreState,
          getActions: getStoreActions,
        };

        const externalAPI = createExternalAPI(storeInstance);
        videoPlayerProps.externalApi(externalAPI);
      }

      console.info('[Video Player] (Config) Loaded', getStoreState().config);
    } catch (error) {
      throw new PlayerError(ErrorCodes.CONFIGURATION);
    }
  }),

  reset: action((state) => {
    Object.assign(state, { ...initialState });
  }),

  setLoaded: action((state, payload) => {
    state.loaded = payload;
  }),

  setAppName: action((state, payload) => {
    state.appName = payload;
  }),

  setAppVersion: action((state, payload) => {
    state.appVersion = payload;
  }),

  setVideoGuid: action((state, payload) => {
    state.videoGuid = payload;
  }),

  setUserGuid: action((state, payload) => {
    state.userGuid = payload;
  }),

  setEntityId: action((state, payload) => {
    state.entityId = payload;
  }),

  setDayId: action((state, payload) => {
    state.dayId = payload;
  }),

  setRecommendationId: action((state, payload) => {
    state.recommendationId = payload;
  }),

  setLanguage: action((state, payload) => {
    state.language = payload;
  }),

  setAutoplay: action((state, payload) => {
    state.autoplay = payload;
  }),

  setMuted: action((state, payload) => {
    state.muted = payload;
  }),

  setIsBODi: action((state, payload) => {
    state.isBODi = payload;
  }),

  setExtraHeartbeatParams: action((state, payload) => {
    state.extraHeartbeatParams = payload;
  }),

  setGetAccessToken: action((state, payload) => {
    state.getAccessToken = payload;
  }),

  setCloseCallback: action((state, payload) => {
    state.closeCallback = payload;
  }),

  setOnPlayCallback: action((state, payload) => {
    state.onPlayCallback = payload;
  }),

  setOnPauseCallback: action((state, payload) => {
    state.onPauseCallback = payload;
  }),

  setOnStopCallback: action((state, payload) => {
    state.onStopCallback = payload;
  }),

  setOnSeekCallback: action((state, payload) => {
    state.onSeekCallback = payload;
  }),

  setOnProgressCallback: action((state, payload) => {
    state.onProgressCallback = payload;
  }),

  setOnKeyPressUpCallback: action((state, payload) => {
    state.onKeyPressUpCallback = payload;
  }),

  setOnResumeOverlayChangeCallback: action((state, payload) => {
    state.onResumeOverlayChangeCallback = payload;
  }),

  setOnAnyActionCallback: action((state, payload) => {
    state.onAnyActionCallback = payload;
  }),

  setOnErrorCallback: action((state, payload) => {
    state.onErrorCallback = payload;
  }),

  setCocoaApiUrl: action((state, payload) => {
    state.cocoaApiUrl = payload;
  }),

  setCocoaApiKey: action((state, payload) => {
    state.cocoaApiKey = payload;
  }),

  setVideoApiRetryAttempts: action((state, payload) => {
    state.videoApiRetryAttempts = payload;
  }),

  setVhsApiUrl: action((state, payload) => {
    state.vhsApiUrl = payload;
  }),

  setVhsApiKey: action((state, payload) => {
    state.vhsApiKey = payload;
  }),

  setMuxEnvKey: action((state, payload) => {
    state.muxEnvKey = payload;
  }),

  setMuxPlayerName: action((state, payload) => {
    state.muxPlayerName = payload;
  }),

  setMuxDisableCookies: action((state, payload) => {
    state.muxDisableCookies = payload;
  }),

  setMuxRespectDoNotTrack: action((state, payload) => {
    state.muxRespectDoNotTrack = payload;
  }),

  setMuxIsEnabled: action((state, payload) => {
    state.muxIsEnabled = payload;
  }),

  setTracksIsEnabled: action((state, payload) => {
    state.tracksIsEnabled = payload;
  }),

  setChromecastAppId: action((state, payload) => {
    state.chromecastAppId = payload;
  }),

  setPlaybackControlsEnabled: action((state, payload) => {
    state.playbackControlsEnabled = payload;
  }),

  setDebug: action((state, payload) => {
    state.debug = payload;
  }),

  setLogging: action((state, payload) => {
    state.logging = payload;
  }),

  setUseBodwebHeader: action((state, payload) => {
    state.useBodwebHeader = payload;
  }),

  setSimulateVideoApiAuthorizer: action((state, payload) => {
    state.simulateVideoApiAuthorizer = payload;
  }),

  setSkipResume: action((state, payload) => {
    state.skipResume = payload;
  }),

  setMaxBitrateForEdge: action((state, payload) => {
    state.maxBitrateForEdge = payload;
  }),

  onPlayerDestroy: thunkOn(
    function observer(_, storeActions) {
      return [storeActions.player.destroy];
    },
    function handler(actions) {
      actions.reset();
    }
  ),
};

const configValidationSchema = yup.object().shape<VideoPlayerProps>({
  appName: yup.string(),
  appVersion: yup.string(),
  userGUID: yup.string(),
  entityId: yup.string(),
  dayId: yup.string(),
  recommendationId: yup.string().nullable(),
  videoGUID: yup.string().required(),
  autoplay: yup.bool(),
  muted: yup.bool(),
  language: yup.string(),
  getAccessToken: yup.mixed<() => Promise<any>>(),
  closeCallback: yup.mixed<() => any>().test({
    name: 'isOptionalFunction',
    exclusive: false,
    test: (value: any) => ['function', 'undefined'].includes(typeof value),
  }),
  config: yup.object().required().shape({
    vhsApiUrl: yup.string().required(),
    vhsApiKey: yup.string().required(),
    muxEnvKey: yup.string().when('muxIsEnabled', {
      is: (muxIsEnabled: boolean | undefined) => muxIsEnabled ?? true,
      then: yup.string().required(),
      otherwise: yup.string().notRequired(),
    }),
    muxPlayerName: yup.string(),
    muxDisableCookies: yup.boolean(),
    muxRespectDoNotTrack: yup.boolean(),
    muxIsEnabled: yup.boolean(),
    tracksIsEnabled: yup.boolean(),
    chromecastAppId: yup.string(),
    debug: yup.boolean(),
    logging: yup.boolean(),
    simulateVideoApiAuthorizer: yup.boolean(),
  }),
});
